// src/pages/Employee/CreateEmployee/index.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, CardHeader, CardTitle, Button, Spinner } from 'reactstrap';
import { ArrowLeft, ArrowRight, Save, X } from 'react-feather';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';
import BreadCrumb from '../../Components/Common/BreadCrumb';

import StepIndicator from '../../Components/employee/stepindicator';
import Step1BasicInfo from '../../Components/employee/step1BasicInfo';
import Step2PersonalFamily from '../../Components/employee/step2PersonalFamily';
import Step3Address from '../../Components/employee/step3Address';
import Step4JobSalaryBank, { LookupOption } from '../../Components/employee/step4JobSalary';
import Step5Qualifications from '../../Components/employee/step5Qualification';
import Step6Documents from '../../Components/employee/step6Documents';
import Step7EmergencyReference from '../../Components/employee/step7Emergency';

import { validateStep } from '../../types/validator';
import { buildEmployeePayload } from '../../Components/employee/buildpayload';
import {
    EmployeeFormData,
    initialEmployeeFormData,
    StepErrors,
    WIZARD_STEPS,
} from '../../types/employee.types';

const LAST_STEP = WIZARD_STEPS.length - 1;

const CreateEmployee: React.FC = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState<EmployeeFormData>(initialEmployeeFormData);
    const [currentStep, setCurrentStep] = useState(0);
    const [furthestStep, setFurthestStep] = useState(0);
    const [stepErrors, setStepErrors] = useState<Record<number, StepErrors>>({});
    const [submitting, setSubmitting] = useState(false);

    // Lookup data for selects on the Job/Salary/Bank step
    const [designations, setDesignations] = useState<LookupOption[]>([]);
    const [units, setUnits] = useState<LookupOption[]>([]);
    const [employeeLocations, setEmployeeLocations] = useState<LookupOption[]>([]);
    const [banks, setBanks] = useState<LookupOption[]>([]);
    const [loadingLookups, setLoadingLookups] = useState(true);
    const [apiValidationError, setApiValidationError] = useState(false);
    const handleStepValidation = (isValid: boolean) => {
        setApiValidationError(!isValid);
    };
    const fetchLookups = useCallback(async () => {
        setLoadingLookups(true);
        try {
            const [designationRes, unitRes, locationRes, bankRes] = await Promise.allSettled([
                apiClient.get('/designations'),
                apiClient.get('/units'),
                apiClient.get('/locations/office/active/all'),
                apiClient.get('/banks/active'),
            ]);

            const unwrap = (res: PromiseSettledResult<any>, labelKey: string): LookupOption[] => {
                if (res.status !== 'fulfilled') return [];
                const raw = res.value?.data || res.value || [];
                const list = Array.isArray(raw) ? raw : raw.data || [];
                return list.map((item: any) => ({ id: item.id, label: item[labelKey] || item.name || item.title || item.office_name || item.bankname}));
            };
            console.log(designationRes, unitRes, locationRes, bankRes)
            setDesignations(unwrap(designationRes, 'title'));
            setUnits(unwrap(unitRes, 'name'));
            setEmployeeLocations(unwrap(locationRes, 'name'));
            setBanks(unwrap(bankRes, 'bank_name'));
        } catch (error) {
            console.error('Error fetching lookup data:', error);
        } finally {
            setLoadingLookups(false);
        }
    }, []);

    useEffect(() => {
        fetchLookups();
    }, [fetchLookups]);

    // --- generic per-step data setters ---
    const updateStepData = <K extends keyof EmployeeFormData>(key: K, value: EmployeeFormData[K]) => {
        setForm((prev: any) => ({ ...prev, [key]: value }));
    };

    const currentErrors = stepErrors[currentStep] || {};

    const stepHasError: Record<number, boolean> = {};
    Object.keys(stepErrors).forEach((k) => {
        stepHasError[Number(k)] = Object.keys(stepErrors[Number(k)]).length > 0;
    });

    const goToStep = (index: number) => {
        if (index < 0 || index > LAST_STEP) return;
        setCurrentStep(index);
    };

    const handleNext = () => {
        // Check local validation
        const errors = validateStep(currentStep, form);
        setStepErrors((prev) => ({ ...prev, [currentStep]: errors }));

        if (Object.keys(errors).length > 0) {
            toast.error('Please fix the highlighted fields before continuing');
            return;
        }

        // Check API validation (for employee code)
        if (apiValidationError) {
            toast.error('Please enter a valid and unique employee code');
            return;
        }

        const next = Math.min(currentStep + 1, LAST_STEP);
        setCurrentStep(next);
        setFurthestStep((prev) => Math.max(prev, next));
    };


    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleStepClick = (index: number) => {
        // Validate the step we're leaving before jumping, so errors surface on
        // the indicator rather than letting the user silently skip past them.
        const errors = validateStep(currentStep, form);
        setStepErrors((prev) => ({ ...prev, [currentStep]: errors }));
        goToStep(index);
    };

    const handleSubmit = async () => {
        // Validate every step before final submission
        let allErrors: Record<number, StepErrors> = {};
        let firstInvalidStep: number | null = null;

        WIZARD_STEPS.forEach((_: any, index: any) => {
            const errors = validateStep(index, form);
            allErrors[index] = errors;
            if (Object.keys(errors).length > 0 && firstInvalidStep === null) {
                firstInvalidStep = index;
            }
        });

        setStepErrors(allErrors);

        if (firstInvalidStep !== null) {
            setCurrentStep(firstInvalidStep);
            setFurthestStep((prev) => Math.max(prev, firstInvalidStep as number));
            toast.error(`Please review Step ${(firstInvalidStep as number) + 1}: ${WIZARD_STEPS[firstInvalidStep as number].label}`);
            return;
        }

        setSubmitting(true);
        try {
            const payload = buildEmployeePayload(form);
            const response = await apiClient.post('/employees', payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Employee created successfully!');
            const data = response?.data || response;
            const newId = data?.id || data?.data?.id;
            navigate(newId ? `/employees/${newId}` : '/employees');
        } catch (error: any) {
            console.error('Error creating employee:', error);
            toast.error(error?.message || 'Failed to create employee');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return <Step1BasicInfo
                    data={form.basicInfo}
                    onChange={(v: any) => updateStepData('basicInfo', v)}
                    errors={currentErrors}
                    onValidationChange={handleStepValidation}
                />
            case 1:
                return (
                    <Step2PersonalFamily
                        data={form.personalFamily}
                        onChange={(v: any) => updateStepData('personalFamily', v)}
                        errors={currentErrors}
                        formData={form}
                    />
                );
            case 2:
                return <Step3Address data={form.address} onChange={(v: any) => updateStepData('address', v)} errors={currentErrors} />;
            case 3:
                return (
                    <Step4JobSalaryBank
                        data={form.jobSalaryBank}
                        onChange={(v: any) => updateStepData('jobSalaryBank', v)}
                        errors={currentErrors}
                        designations={designations}
                        units={units}
                        employeeLocations={employeeLocations}
                        banks={banks}
                        loadingLookups={loadingLookups}
                    />
                );
            case 4:
                return (
                    <Step5Qualifications
                        data={form.qualifications}
                        onChange={(v: any) => updateStepData('qualifications', v)}
                        errors={currentErrors}
                    />
                );
            case 5:
                return <Step6Documents data={form.documents} onChange={(v: any) => updateStepData('documents', v)} errors={currentErrors} />;
            case 6:
                return (
                    <Step7EmergencyReference
                        data={form.emergencyReference}
                        onChange={(v: any) => updateStepData('emergencyReference', v)}
                        errors={currentErrors}
                    />
                );
            default:
                return null;
        }
    };

    const isLastStep = currentStep === LAST_STEP;

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Create Employee" pageTitle="Employees" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <CardTitle tag="h5" className="mb-0">
                                    New Employee
                                </CardTitle>
                                <Button color="secondary" size="sm" onClick={() => navigate('/employees')}>
                                    <X size={14} className="me-1" />
                                    Cancel
                                </Button>
                            </CardHeader>

                            <CardBody>
                                <StepIndicator
                                    steps={WIZARD_STEPS}
                                    currentIndex={currentStep}
                                    furthestIndex={furthestStep}
                                    stepHasError={stepHasError}
                                    onStepClick={handleStepClick}
                                />

                                <div className="border-top pt-4">{renderStep()}</div>

                                <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-4">
                                    <Button color="secondary" outline onClick={handleBack} disabled={currentStep === 0 || submitting}>
                                        <ArrowLeft size={14} className="me-1" />
                                        Back
                                    </Button>

                                    <span className="text-muted small">
                                        Step {currentStep + 1} of {WIZARD_STEPS.length}
                                    </span>

                                    {!isLastStep && (
                                        <Button color="primary" onClick={handleNext} disabled={submitting}>
                                            Next
                                            <ArrowRight size={14} className="ms-1" />
                                        </Button>
                                    )}

                                    {isLastStep && (
                                        <Button color="success" onClick={handleSubmit} disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <Save size={14} className="me-1" />
                                                    Create Employee
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default CreateEmployee;